import type { Manifest } from '../adapter/platform.ts';
import { type PlaceState, describeReadings } from '../adapter/state.ts';

/* The light web UI, for public pages and the browser only. Inside the app the app draws lists, details
 * and booking natively, and this UI stays hidden. */

const KIND_LABELS: Record<string, string> = {
  meeting_room: 'Meeting room',
  desk: 'Desk',
  workspace_area: 'Workspace',
  parking_spot: 'Parking',
  lobby: 'Lobby',
  point_of_interest: 'Point of interest',
  unit: 'Unit',
};

const element = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  text?: string,
  className?: string,
): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  if (text) node.textContent = text;
  if (className) node.className = className;
  return node;
};

export interface Panel {
  showDetail(code: string): void;
  setStates(next: Map<string, PlaceState>): void;
}

export function createPanel(
  root: HTMLElement,
  manifest: Manifest,
  actions: { floor(number: number | null): void; select(code: string): void },
): Panel {
  const header = element('header');
  header.append(
    element('h1', manifest.building.name),
    element('p', manifest.mode === 'public' ? 'Public view' : 'Live', 'muted'),
  );
  const floors = element('nav');
  floors.setAttribute('aria-label', 'Floors');
  const numbers = [
    ...new Set(manifest.places.map((place) => place.floorNumber).filter((number) => number !== null)),
  ].toSorted((a, b) => a - b);
  const all = element('button', 'All');
  all.addEventListener('click', () => actions.floor(null));
  floors.append(all);
  for (const number of numbers) {
    const button = element('button', number === 0 ? 'Ground' : `Floor ${number}`);
    button.addEventListener('click', () => actions.floor(number));
    floors.append(button);
  }
  const search = element('input');
  search.type = 'search';
  search.placeholder = 'Find a room or desk';
  search.setAttribute('aria-label', 'Find a place');
  const list = element('ul');
  const detail = element('section', '', 'detail');
  detail.hidden = true;

  const states = new Map<string, PlaceState>();
  const render = (): void => {
    const query = search.value.trim().toLowerCase();
    list.replaceChildren(
      ...manifest.places
        .filter(
          (place) =>
            place.nodeName &&
            (!query || place.name.toLowerCase().includes(query) || place.code.toLowerCase().includes(query)),
        )
        .map((place) => {
          const item = element('li');
          const button = element('button', place.name);
          const state = states.get(place.code);
          if (state && state.booking !== 'not_bookable') button.dataset.booking = state.booking;
          button.addEventListener('click', () => actions.select(place.code));
          item.append(button, element('span', KIND_LABELS[place.kind] ?? place.kind, 'muted'));
          return item;
        }),
    );
  };
  search.addEventListener('input', render);
  root.append(header, floors, search, list, detail);
  render();

  let shown: string | null = null;
  const api = {
    showDetail(code: string): void {
      shown = code;
      const place = manifest.places.find((candidate) => candidate.code === code);
      if (!place) return;
      const state = states.get(code);
      const lines = [KIND_LABELS[place.kind] ?? place.kind];
      if (state?.booking === 'free') lines.push('Free now');
      if (state?.booking === 'booked') lines.push('Booked now');
      if (state) lines.push(...describeReadings(state.readings));
      detail.replaceChildren(element('h2', place.name), ...lines.map((line) => element('p', line)));
      detail.hidden = false;
    },
    setStates(next: Map<string, PlaceState>): void {
      states.clear();
      for (const [code, state] of next) states.set(code, state);
      render();
      if (shown) api.showDetail(shown);
    },
  };
  return api;
}
